import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import FeaturedLayout from '../layouts/FeaturedLayout';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { supabase } from '../../lib/supabase';
import { signImagesDeep } from '../../lib/supabaseClient';
import { formatLocalDate } from '../../lib/dateUtils';
import { numeroEnGrupo } from '../../lib/numerosGrupo';
import { useOrganization } from '../../contexts/OrganizationContext';
import { logoDelTorneo } from '../../lib/logoTorneo';
import { esTorneoExterno } from '../../lib/torneoOrigen';
import { coachesDesdeInscripcion } from '../../lib/staffInscripcion';
import { getStaffInscripcionRpc } from '../../lib/services/inscripciones';
import { resolveSportLabel } from '../../utils/tarjetonPdf';

const resolveGroupRole = (value) => {
  const normalized = String(value || '').toLowerCase();
  if (normalized.includes('head')) return 'Head Coach';
  if (normalized.includes('assistant') || normalized.includes('asistente')) return 'Asistente';
  if (normalized.includes('manager')) return 'Manager';
  return 'Coach';
};

const sanitizeFile = (value) =>
  String(value || '')
    .replace(/\s+/g, '_')
    .replace(/[^a-zA-Z0-9_-]/g, '');

const NAME_PARTICLES = new Set([
  'de', 'del', 'la', 'las', 'los', 'y', 'e', 'o', 'u', 'da', 'dos', 'das'
]);

const toTitleCaseEs = (value) => {
  const raw = String(value || '').trim().replace(/\s+/g, ' ');
  if (!raw) return '';
  return raw.split(' ').map((word, index) => {
    if (!word) return '';
    const hasSymbol = /[0-9./-]/.test(word);
    if (hasSymbol) return word;
    const lower = word.toLocaleLowerCase('es-MX');
    if (index > 0 && NAME_PARTICLES.has(lower)) return lower;
    const first = lower.charAt(0).toLocaleUpperCase('es-MX');
    return `${first}${lower.slice(1)}`;
  }).join(' ');
};

const resolvePlayerMetaValue = (player, metaKey) => {
  const entries = Array.isArray(player?.jugadores_meta)
    ? player.jugadores_meta
    : player?.jugadores_meta
      ? [player.jugadores_meta]
      : [];
  const match = entries.find((item) => item?.meta_key === metaKey);
  if (!match) return '';
  const value = match.meta_value;
  if (value == null) return '';
  if (Array.isArray(value)) {
    return value.filter(Boolean).join(', ');
  }
  return String(value);
};

const previewStyles = `
  .tarjeton-preview-wrapper {
    overflow-x: auto;
    background: #f4f5f7;
    padding: 6px 12px;
  }
  .tarjeton-preview {
    background: transparent;
    width: max-content;
    min-width: max-content;
    margin: 0 auto;
    display: inline-flex;
    flex-direction: column;
    align-items: center;
  }
  @media screen and (max-width: 1024px) {
    .tarjeton-preview .tarjeton-page {
      zoom: 0.9;
    }
  }
  @media screen and (max-width: 768px) {
    .tarjeton-preview .tarjeton-page {
      zoom: 0.9;
    }
  }
  @media screen and (max-width: 480px) {
    .tarjeton-preview .tarjeton-page {
      zoom: 0.82;
    }
  }
  .tarjeton-page {
    width: 816px;
    height: 1056px;
    background: #fff;
    margin: 0 auto 6px;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
    padding: 32px 36px;
    box-sizing: border-box;
    page-break-after: always;
    display: flex;
    flex-direction: column;
    position: relative;
  }
  .hoja-header-split {
    display: grid;
    grid-template-columns: 1fr 1fr 2.6fr 1fr;
    gap: 16px;
    align-items: center;
    margin-bottom: 16px;
  }
  .logo-center {
    width: 120px;
    height: 120px;
  }
  .hoja-info {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
  .hoja-info .equipo-box {
    margin-bottom: 0;
  }
  .hoja-info .meta-row {
    gap: 10px;
  }
  .tarjeton-front-logos {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
  }
  .logo-box {
    width: 120px;
    height: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .logo-left {
    justify-self: start;
  }
  .logo-right {
    justify-self: end;
  }
  .logo-box img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
  .logo-placeholder {
    width: 80px;
    height: 80px;
    border: 1px dashed #9ca3af;
  }
  .equipo-box {
    border: 2px solid #111827;
    text-align: center;
    font-weight: 700;
    padding: 8px 12px;
    margin-bottom: 12px;
  }
  .meta-row {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    text-align: center;
  }
  .meta-label {
    font-weight: 600;
    margin-bottom: 4px;
  }
  .meta-value {
    color: #b91c1c;
    font-weight: 700;
    border: 2px solid #111827;
    padding: 4px 6px;
  }
  .hoja-table {
    width: auto;
    max-width: 100%;
    margin: 0 auto;
    border-collapse: collapse;
    font-size: 11px;
    table-layout: fixed;
  }
  .hoja-table tbody tr {
    height: 30px;
  }
  .hoja-table th,
  .hoja-table td {
    border: 1px solid #111827;
    padding: 0 6px;
    vertical-align: middle;
    word-break: break-word;
  }
  .hoja-table th:nth-child(1),
  .hoja-table td:nth-child(1) {
    width: 60px;
    text-align: center;
  }
  .hoja-table th:nth-child(2),
  .hoja-table td:nth-child(2) {
    width: 195px;
  }
  .hoja-table th:nth-child(3),
  .hoja-table td:nth-child(3) {
    width: 80px;
  }
  .hoja-table th:nth-child(4),
  .hoja-table td:nth-child(4) {
    width: 82px;
  }
  .hoja-table th:nth-child(5),
  .hoja-table td:nth-child(5) {
    width: 108px;
  }
  .hoja-table th:nth-child(6),
  .hoja-table td:nth-child(6) {
    width: 150px;
    white-space: nowrap;
    word-break: normal;
    overflow-wrap: normal;
  }
  .hoja-table th {
    background: #e5e7eb;
    text-transform: uppercase;
    font-size: 10px;
    letter-spacing: 0.04em;
  }
  .hoja-footer {
    margin-top: 12px;
    display: flex;
    justify-content: space-between;
    font-size: 11px;
    height: 32px;
    align-items: flex-end;
  }
  .hoja-page .hoja-footer {
    margin-bottom: 12px;
  }
  .firma-line {
    border-top: 1px solid #111827;
    width: 45%;
    padding-top: 4px;
    text-align: center;
  }
  .hoja-spacer {
    flex: 1;
  }
  .hoja-page-number {
    position: absolute;
    bottom: 12px;
    right: 36px;
    font-size: 10px;
    color: #6b7280;
  }
  .tarjeton-front {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 18px;
    padding-top: 20px;
  }
  .tarjeton-front-body {
    flex: 1;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 18px;
  }
  .tarjeton-front-hero {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    width: 100%;
    margin-bottom: 10px;
  }
  .tarjeton-front-hero-line {
    height: 6px;
    background: #6b7280;
  }
  .tarjeton-front-logo {
    width: 280px;
    height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .tarjeton-front-logo img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
  .tarjeton-front-title {
    font-size: 20px;
    font-weight: 600;
    margin-top: -4px;
  }
  .tarjeton-front-blocks {
    width: 85%;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .tarjeton-line-row {
    display: grid;
    grid-template-columns: 1fr 120px;
    gap: 12px;
  }
  .tarjeton-line {
    border: 2px solid #111827;
    padding: 6px 10px;
    font-weight: 600;
    text-align: center;
  }
  .tarjeton-label {
    font-size: 12px;
    font-weight: 700;
    margin-top: 4px;
  }
  .tarjeton-line-label {
    font-size: 12px;
    font-weight: 700;
    margin-top: 4px;
  }
  .tarjeton-line-row-2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
  .tarjeton-signatures {
    width: 80%;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
    margin-top: 4px;
    font-size: 11px;
    text-align: center;
  }
  .tarjeton-signatures .sig-line {
    border-top: 1px solid #111827;
    padding-top: 6px;
    min-height: 40px;
  }
  .tarjeton-front-footer {
    width: 80%;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
    font-size: 11px;
    margin-bottom: 40px;
    align-items: end;
  }
  .tarjeton-front-footer .footer-item {
    text-align: center;
  }
  .tarjeton-front-footer .footer-line {
    border-bottom: 1px solid #111827;
    min-height: 18px;
    margin-top: 6px;
  }
  .player-count {
    font-weight: 500;
    font-size: 12px;
    color: #374151;
  }
  .tarjeton-coaches {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    margin-bottom: 10px;
  }
  .coach-card {
    border: 2px solid #2563eb;
    display: grid;
    grid-template-columns: 100px 1fr;
    gap: 6px;
    padding: 8px;
    min-height: 120px;
  }
  .coach-info h3 {
    font-weight: 700;
    margin-bottom: 6px;
  }
  .coach-info p {
    font-size: 10px;
  }
  .coach-info strong {
    font-weight: 700;
  }
  .tarjeton-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 6px;
    align-content: start;
  }
  .tarjeton-card {
    border: 2px solid #111827;
    display: grid;
    grid-template-columns: 100px 1fr;
    grid-template-rows: auto auto;
    gap: 6px;
    padding: 6px;
    height: 186px;
    page-break-inside: avoid;
    break-inside: avoid;
  }
  .tarjeton-card .photo-box {
    grid-row: 1;
  }
  .tarjeton-card .card-info {
    grid-row: 1;
  }
  .photo-box {
    border: 1px solid #111827;
    width: 100%;
    height: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }
  .photo-box img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .photo-placeholder {
    width: 100%;
    height: 100%;
    border: 1px dashed #9ca3af;
  }
  .card-info p {
    font-size: 9px;
    margin: 1px 0;
  }
  .card-info strong {
    font-weight: 700;
  }
  .card-info .value-line {
    border-bottom: 1px solid #111827;
    padding-left: 6px;
    min-height: 16px;
    display: inline-block;
    min-width: 120px;
  }
  .card-footer {
    grid-column: 1 / -1;
    display: flex;
    justify-content: space-between;
    gap: 24px;
    font-size: 9px;
    margin-top: -10px;
    align-items: flex-end;
  }
  .card-footer .footer-field {
    width: 48%;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .card-footer .footer-line {
    border-bottom: 1px solid #111827;
    min-height: 12px;
  }
`;

const printStyles = `
  ${previewStyles}
  @page {
    size: letter portrait;
    margin: 0;
  }
  html, body {
    margin: 0 !important;
    padding: 0 !important;
    background: #ffffff !important;
  }
  body {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .tarjeton-preview-wrapper {
    background: #ffffff !important;
    padding: 0 !important;
    overflow: visible !important;
  }
  .tarjeton-preview {
    background: transparent !important;
    width: auto !important;
    min-width: 0 !important;
    margin: 0 auto !important;
    display: block !important;
  }
  .tarjeton-page {
    width: 816px !important;
    height: 1056px !important;
    min-height: 1056px !important;
    background: #fff !important;
    margin: 0 auto !important;
    box-shadow: none !important;
    padding: 32px 36px !important;
    box-sizing: border-box !important;
    page-break-after: always !important;
    break-after: page !important;
    page-break-inside: avoid !important;
    break-inside: avoid !important;
    display: flex !important;
    flex-direction: column !important;
    position: relative !important;
    overflow: hidden !important;
    transform: none !important;
  }
  .tarjeton-page:last-child {
    page-break-after: auto !important;
    break-after: auto !important;
  }
`;

const TarjetonPreview = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { orgName, orgLogo } = useOrganization();
  const groupId = searchParams.get('grupoId');
  const torneoId = searchParams.get('torneoId');
  const previewRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fileName, setFileName] = useState('tarjeton_ltl_kids');
  const [tarjetonData, setTarjetonData] = useState(null);

  const loadTarjeton = useCallback(async () => {
    if (!groupId || !torneoId) {
      setError('Faltan datos para generar el tarjetón.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const [{ data: grupo, error: grupoError }, { data: torneo, error: torneoError }] = await Promise.all([
        supabase
          .from('grupos')
          .select('id, nombre_del_grupo, deporte, categoria_base, rama, org_id')
          .eq('id', groupId)
          .single(),
        supabase
          .from('torneos')
          .select('id, nombre, logo_url, org_organizadora_id, liga:ligas(nombre, logo_url)')
          .eq('id', torneoId)
          .single()
      ]);

      if (grupoError) throw grupoError;
      if (torneoError) throw torneoError;

      const { data: playersData, error: playersError } = await supabase
        .from('jugadores_grupos')
        .select(
          `numero_jersey,
          jugadores(
            id,
            nombre_completo,
            numero,
            curp,
            fecha_nacimiento,
            imagen_url,
            imagen_path,
            activo,
            estatus,
            deleted,
            jugadores_meta(
              meta_key,
              meta_value
            )
          )`
        )
        .eq('grupo_id', groupId)
        .eq('activo', true)
        .eq('deleted', false)
        .eq('jugadores.activo', true)
        .eq('jugadores.deleted', false)
        .neq('jugadores.estatus', 'Baja')
        .order('nombre_completo', { foreignTable: 'jugadores', ascending: true });

      if (playersError) throw playersError;

      const players = (playersData || [])
        .filter((row) => row?.jugadores)
        .map((row) => ({
          ...row.jugadores,
          // El numero del grupo manda sobre el del club (misma regla que uniformes).
          numero: numeroEnGrupo(row, row.jugadores),
        }))
        .map((player) => ({
          ...player,
          jugadores_meta: Array.isArray(player.jugadores_meta)
            ? player.jugadores_meta
            : player.jugadores_meta
              ? [player.jugadores_meta]
              : [],
          grado_escolar: resolvePlayerMetaValue(player, 'grado_escolar')
        }));

      // Bucket privado (BI-2): firmar fotos desde imagen_path
      await signImagesDeep(players);

      const { data: staffAssignments, error: staffError } = await supabase
        .from('staff_grupos')
        .select(
          `rol,
           tarjeton_entrenador,
           tarjeton_asistente,
           staff(
             id,
             nombre_completo,
             imagen_url,
             imagen_path,
             email
           )`
        )
        .eq('grupo_id', groupId)
        .eq('activo', true);

      if (staffError) {
        console.warn('No se pudo cargar el staff del grupo para tarjetón:', staffError);
      }

      const assignments = staffAssignments || [];
      await signImagesDeep(assignments);
      let headCoach = assignments.find((rel) => rel.tarjeton_entrenador === true)
        || assignments.find((rel) => resolveGroupRole(rel.rol) === 'Head Coach');
      let assistantCoach = assignments.find((rel) => rel.tarjeton_asistente === true)
        || assignments.find((rel) => resolveGroupRole(rel.rol) === 'Asistente');

      // En un torneo de la RED el staff que va al torneo es el DECLARADO en la
      // inscripcion (torneos_inscripciones_staff, 20260907), no todo el del grupo: es lo
      // que la organizadora ve. En el interno (o sin inscripcion) sigue el del grupo.
      if (esTorneoExterno(torneo, grupo?.org_id)) {
        const { data: insc } = await supabase
          .from('torneos_inscripciones')
          .select('id')
          .eq('torneo_id', torneoId)
          .eq('grupo_id', groupId)
          .is('deleted_at', null)
          .limit(1)
          .maybeSingle();
        if (insc?.id) {
          const { data: declarados } = await getStaffInscripcionRpc(insc.id);
          const desdeInsc = coachesDesdeInscripcion(declarados);
          headCoach = desdeInsc.headCoach;
          assistantCoach = desdeInsc.assistantCoach;
        }
      }

      const name = `Tarjeton_${sanitizeFile(torneo?.nombre || 'Torneo')}_${sanitizeFile(
        grupo?.nombre_del_grupo || 'Grupo'
      )}`;
      setFileName(name);

      setTarjetonData({
        grupo,
        torneo,
        players,
        staff: {
          headCoach,
          assistantCoach
        }
      });
    } catch (err) {
      console.error('Error generando tarjetón:', err);
      setError('No se pudo generar el tarjetón.');
    } finally {
      setLoading(false);
    }
  }, [groupId, torneoId]);

  useEffect(() => {
    loadTarjeton();
  }, [loadTarjeton]);

  const formatFecha = useCallback((value) => {
    if (!value) return '';
    try {
      return formatLocalDate(value, 'd MMMM yyyy');
    } catch (err) {
      return '';
    }
  }, []);

  const pages = useMemo(() => {
    if (!tarjetonData) return { hoja: [], tarjeton: [] };
    const { players } = tarjetonData;
    const hojaPages = [];
    const hojaSize = 20;
    for (let i = 0; i < players.length; i += hojaSize) {
      hojaPages.push(players.slice(i, i + hojaSize));
    }

    const tarjetonPages = [];
    const firstPageSize = 8;
    const otherPageSize = 8;
    tarjetonPages.push(players.slice(0, firstPageSize));
    let idx = firstPageSize;
    while (idx < players.length) {
      tarjetonPages.push(players.slice(idx, idx + otherPageSize));
      idx += otherPageSize;
    }

    return { hoja: hojaPages, tarjeton: tarjetonPages };
  }, [tarjetonData]);

  const waitForImages = useCallback(async (doc) => {
    const images = Array.from(doc.images || []);
    await Promise.all(images.map((img) => new Promise((resolve) => {
      if (img.complete) {
        resolve();
        return;
      }
      img.onload = () => resolve();
      img.onerror = () => resolve();
    })));
  }, []);

  const handlePrint = useCallback(async () => {
    const previewNode = previewRef.current;
    if (!previewNode) return;

    const printWindow = window.open('', '_blank', 'width=1100,height=900');
    if (!printWindow) return;

    const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

    const printableMarkup = previewNode.outerHTML;

    printWindow.document.open();
    printWindow.document.write(`
      <!doctype html>
      <html lang="es">
        <head>
          <meta charset="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <title>${escapeHtml(fileName)}</title>
          <style>${printStyles}</style>
        </head>
        <body>${printableMarkup}</body>
      </html>
    `);
    printWindow.document.close();

    await waitForImages(printWindow.document);
    printWindow.focus();
    printWindow.print();
  }, [fileName, waitForImages]);

  return (
    <FeaturedLayout
      title="Tarjetón"
      subtitle="Vista previa del documento"
      refreshAction={!loading ? loadTarjeton : undefined}
    >
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="text-sm text-gray-500">{fileName}</div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
              Volver
            </Button>
            <Button size="sm" onClick={handlePrint} disabled={!tarjetonData}>
              Imprimir
            </Button>
          </div>
        </div>
        {loading && <div className="text-sm text-gray-500">Generando tarjetón...</div>}
        {!loading && error && <div className="text-sm text-red-600">{error}</div>}
        {!loading && !error && tarjetonData && (
          <div className="tarjeton-preview-wrapper">
            <div ref={previewRef} className="tarjeton-preview space-y-6">
              <style>{previewStyles}</style>

              {pages.hoja.map((pagePlayers, pageIndex) => (
                <section key={`hoja-${pageIndex}`} className="tarjeton-page hoja-page">
                  <div className="hoja-header hoja-header-split">
                    <div className="logo-box logo-left">
                      {logoDelTorneo(tarjetonData.torneo) ? (
                        <img src={logoDelTorneo(tarjetonData.torneo)} alt="Torneo" />
                      ) : (
                        <div className="logo-placeholder" />
                      )}
                    </div>
                    <div className="logo-box logo-center">
                      <img src={orgLogo} alt={orgName} />
                    </div>
                    <div className="hoja-info">
                      <div className="equipo-box">EQUIPO: {orgName}</div>
                      <div className="meta-row">
                        <div>
                          <div className="meta-label">Deporte:</div>
                          <div className="meta-value">{tarjetonData.grupo?.deporte ? resolveSportLabel(tarjetonData.grupo.deporte) : ''}</div>
                        </div>
                        <div>
                          <div className="meta-label">Categoría:</div>
                          <div className="meta-value">{tarjetonData.grupo?.categoria_base || ''}</div>
                        </div>
                        <div>
                          <div className="meta-label">Rama:</div>
                          <div className="meta-value">{tarjetonData.grupo?.rama || ''}</div>
                        </div>
                      </div>
                    </div>
                    <div className="logo-box logo-right">
                      {logoDelTorneo(tarjetonData.torneo) ? (
                        <img src={logoDelTorneo(tarjetonData.torneo)} alt="Torneo" />
                      ) : (
                        <div className="logo-placeholder" />
                      )}
                    </div>
                  </div>
                  <table className="hoja-table">
                    <thead>
                      <tr>
                        <th>No. en playera</th>
                        <th>Nombre del Jugador</th>
                        <th>Equipo</th>
                        <th>Grado Escolar</th>
                        <th>Fecha de nacimiento</th>
                        <th>CURP</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagePlayers.map((player) => (
                        <tr key={player.id}>
                          <td>{player.numero || ''}</td>
                          <td className="whitespace-normal break-words leading-tight py-1">
                            {toTitleCaseEs(player.nombre_completo)}
                          </td>
                          <td>{orgName}</td>
                          <td>{player.grado_escolar || ''}</td>
                          <td>{formatFecha(player.fecha_nacimiento)}</td>
                          <td>{player.curp || ''}</td>
                        </tr>
                      ))}
                      {Array.from({ length: Math.max(0, 20 - pagePlayers.length) }).map((_, idx) => (
                        <tr key={`empty-${idx}`} className="empty-row">
                          <td></td>
                          <td></td>
                          <td></td>
                          <td></td>
                          <td></td>
                          <td></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="hoja-spacer" />
                  <div className="hoja-footer">
                    <div className="firma-line">Fecha</div>
                    <div className="firma-line">Firma del delegado</div>
                  </div>
                  <div className="hoja-page-number">Hoja {pageIndex + 1}</div>
                </section>
              ))}

              <section className="tarjeton-page tarjeton-front">
                <div className="tarjeton-front-body">
                  <div className="tarjeton-front-hero">
                    <div className="tarjeton-front-hero-line" />
                    <div className="tarjeton-front-logo">
                      {logoDelTorneo(tarjetonData.torneo) ? (
                        <img src={logoDelTorneo(tarjetonData.torneo)} alt="Torneo" />
                      ) : (
                        <div className="logo-placeholder" />
                      )}
                    </div>
                    <div className="tarjeton-front-hero-line" />
                  </div>
                  <div className="tarjeton-front-title">Cédula de Inscripción</div>
                  <div className="tarjeton-front-blocks">
                    <div>
                      <div className="tarjeton-line-row">
                        <div>
                          <div className="tarjeton-line">EQUIPO: {orgName}</div>
                          <div className="tarjeton-line-label">Institución</div>
                        </div>
                        <div>
                          <div className="tarjeton-line">{tarjetonData.players.length}</div>
                          <div className="tarjeton-line-label">No. de jugadores</div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="tarjeton-line-row-2">
                        <div>
                          <div className="tarjeton-line">{tarjetonData.grupo?.deporte ? resolveSportLabel(tarjetonData.grupo.deporte) : ''}</div>
                          <div className="tarjeton-label">Deporte</div>
                        </div>
                        <div>
                          <div className="tarjeton-line">{tarjetonData.grupo?.rama || ''}</div>
                          <div className="tarjeton-label">Rama</div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="tarjeton-line">{tarjetonData.grupo?.categoria_base || ''}</div>
                      <div className="tarjeton-label">Categoría</div>
                    </div>
                  </div>
                </div>
                <div className="tarjeton-signatures">
                  <div className="sig-line">Nombre y Firma del Presidente LTL</div>
                  <div className="sig-line">Nombre y Firma del Presidente / Encargado del Equipo</div>
                </div>
                <div className="tarjeton-front-footer">
                  <div className="footer-item">
                    &nbsp;
                    <div className="footer-line" />
                  </div>
                  <div className="footer-item">
                    Fecha
                    <div className="footer-line" />
                  </div>
                </div>
              </section>

              {pages.tarjeton.map((pagePlayers, pageIndex) => (
                <section key={`tarjeton-${pageIndex}`} className="tarjeton-page tarjeton-players">
                  {pageIndex === 0 && (
                    <div className="tarjeton-coaches">
                      <div className="coach-card">
                        <div className="photo-box">
                          {tarjetonData.staff.headCoach?.staff?.imagen_url ? (
                            <img src={tarjetonData.staff.headCoach.staff.imagen_url} alt="Entrenador" />
                          ) : (
                            <div className="photo-placeholder" />
                          )}
                        </div>
                        <div className="coach-info">
                          <h3>Entrenador</h3>
                          <p><strong>Nombre:</strong> {toTitleCaseEs(tarjetonData.staff.headCoach?.staff?.nombre_completo)}</p>
                          <p><strong>Tel. Oficina:</strong> </p>
                          <p><strong>Tel. Part.:</strong> </p>
                          <p><strong>Entrenador Asistente:</strong> </p>
                        </div>
                      </div>
                      <div className="coach-card">
                        <div className="photo-box">
                          {tarjetonData.staff.assistantCoach?.staff?.imagen_url ? (
                            <img src={tarjetonData.staff.assistantCoach.staff.imagen_url} alt="Asistente" />
                          ) : (
                            <div className="photo-placeholder" />
                          )}
                        </div>
                        <div className="coach-info">
                          <h3>Asistente</h3>
                          <p><strong>Nombre:</strong> {toTitleCaseEs(tarjetonData.staff.assistantCoach?.staff?.nombre_completo)}</p>
                          <p><strong>Tel. Oficina:</strong> </p>
                          <p><strong>Tel. Part.:</strong> </p>
                          <p><strong>Delegado Asistente:</strong> </p>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="tarjeton-grid">
                    {pagePlayers.map((player) => (
                      <div key={player.id} className="tarjeton-card">
                        <div className="photo-box">
                          {player.imagen_url ? (
                            <img src={player.imagen_url} alt={toTitleCaseEs(player.nombre_completo)} />
                          ) : (
                            <div className="photo-placeholder" />
                          )}
                        </div>
                        <div className="card-info">
                          <p><strong>No. de Control</strong> <span className="value-line" /></p>
                          <p><strong>Equipo</strong> <span className="value-line">{orgName}</span></p>
                          <p><strong>Nombre</strong> <span className="value-line">{toTitleCaseEs(player.nombre_completo)}</span></p>
                          <p><strong>CURP</strong> <span className="value-line">{player.curp || ''}</span></p>
                          <p><strong>Nivel</strong> <span className="value-line" /></p>
                          <p><strong>Grado Escolar</strong> <span className="value-line">{player.grado_escolar || ''}</span></p>
                          <p><strong>No. de Playera</strong> <span className="value-line">{player.numero || ''}</span></p>
                        </div>
                        <div className="card-footer">
                          <div className="footer-field">
                            <div className="footer-line">{formatFecha(player.fecha_nacimiento)}</div>
                            <span>Fecha de Nacimiento</span>
                          </div>
                          <div className="footer-field">
                            <div className="footer-line" />
                            <span>Firma</span>
                          </div>
                        </div>
                      </div>
                    ))}
                    {Array.from({ length: Math.max(0, 8 - pagePlayers.length) }).map(
                      (_, idx) => (
                        <div key={`empty-card-${idx}`} className="tarjeton-card empty">
                          <div className="photo-box">
                            <div className="photo-placeholder" />
                          </div>
                          <div className="card-info">
                            <p><strong>No. de Control</strong> <span className="value-line" /></p>
                            <p><strong>Equipo</strong> <span className="value-line" /></p>
                            <p><strong>Nombre</strong> <span className="value-line" /></p>
                            <p><strong>CURP</strong> <span className="value-line" /></p>
                            <p><strong>Nivel</strong> <span className="value-line" /></p>
                            <p><strong>Grado Escolar</strong> <span className="value-line" /></p>
                            <p><strong>No. de Playera</strong> <span className="value-line" /></p>
                          </div>
                          <div className="card-footer">
                            <div className="footer-field">
                              <div className="footer-line" />
                              <span>Fecha de Nacimiento</span>
                            </div>
                            <div className="footer-field">
                              <div className="footer-line" />
                              <span>Firma</span>
                            </div>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </section>
              ))}
            </div>
          </div>
        )}
      </Card>
    </FeaturedLayout>
  );
};

export default TarjetonPreview;
